import PageSkeleton from "@/components/page-skeleton";
import { TeamCreditsProgress } from "@/components/team-credits/team-credits-progress";
import { DialogPurchaseCredits } from "@/components/team/dialog-purchase-credits";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { useToast, useToastError } from "@/components/ui/use-toast";
import { useCredits } from "@/contexts/credits-provider";
import { TeamCreditsStackSourceEnum } from "@/enums/team-credits";
import { useApi } from "@/hooks/useApi";
import { useTranslation } from "@/hooks/useTranslation";
import { TeamCreditsStackDTO } from "@shaarp/common";
import { ArrowUpIcon, BadgeCentIcon, Loader2Icon } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useQueryParam } from "use-query-params";

export default function TeamCreditsPage() {
  const api = useApi();
  const toastError = useToastError();
  const { toast } = useToast();
  const { t } = useTranslation();
  const [, setSearchParams] = useSearchParams();
  const {
    creditsBalance,
    isLoading: isLoadingCredits,
    refreshCredits,
  } = useCredits();

  const [giftedStack, setGiftedStack] = useState<TeamCreditsStackDTO>();
  const [purchaseStack, setPurchaseStack] = useState<TeamCreditsStackDTO>();
  const [isLoading, setIsLoading] = useState(true);
  const [isPurchaseDialogOpen, setIsPurchaseDialogOpen] = useState(false);
  const processedCheckoutRef = useRef<string | null>(null);
  const [pendingToast, setPendingToast] = useState<{ credits: number } | null>(
    null
  );
  const [checkoutQueryValue] = useQueryParam<string>("checkout");

  const fetchStacks = useCallback(async () => {
    try {
      setIsLoading(true);
      const { data } = await api.get<TeamCreditsStackDTO[]>(
        "/my/team/credits/stacks"
      );
      setGiftedStack(
        data.find((stack) => stack.source === TeamCreditsStackSourceEnum.GIFT)
      );
      setPurchaseStack(
        data.find(
          (stack) => stack.source === TeamCreditsStackSourceEnum.PURCHASE
        )
      );
    } catch (error) {
      toastError();
    } finally {
      setIsLoading(false);
    }
  }, [api, toastError]);

  useEffect(() => {
    if (!checkoutQueryValue) {
      void fetchStacks();
      void refreshCredits();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle checkout redirects
  useEffect(() => {
    // Reset ref if checkout status is cleared
    if (!checkoutQueryValue) {
      processedCheckoutRef.current = null;
      return;
    }

    // Skip if we've already processed this exact status
    if (processedCheckoutRef.current === checkoutQueryValue) {
      return;
    }

    // Mark as processed to prevent re-execution
    processedCheckoutRef.current = checkoutQueryValue;

    if (checkoutQueryValue === "success") {
      // If this window was opened by window.open(), notify the opener and close
      if (window.opener && !window.opener.closed) {
        window.opener.postMessage(
          { type: "STRIPE_CHECKOUT_SUCCESS" },
          window.location.origin
        );
        window.close();
        return;
      }

      // Get credits amount from sessionStorage before cleaning it up
      const creditsStr = sessionStorage.getItem("stripe-checkout-credits");
      const credits = creditsStr ? parseInt(creditsStr, 10) : 0;

      // Clean up sessionStorage first
      sessionStorage.removeItem("stripe-checkout-credits");

      // Set pending toast to trigger display in separate effect
      const creditsToShow = !isNaN(credits) && credits > 0 ? credits : 0;
      setPendingToast({ credits: creditsToShow });

      // Refresh credits immediately and then again after a delay to allow webhook to process
      // Use void to explicitly mark async calls
      void refreshCredits();
      void fetchStacks();

      setTimeout(() => {
        void refreshCredits();
        void fetchStacks();
      }, 2000);

      // Remove query param after everything is set up
      setSearchParams(
        (params) => {
          const newParams = new URLSearchParams(params);
          newParams.delete("checkout");
          return newParams;
        },
        { replace: true }
      );
    } else if (checkoutQueryValue === "cancelled") {
      // If this window was opened by window.open(), close it
      if (window.opener && !window.opener.closed) {
        window.close();
        return;
      }

      // Clean up sessionStorage on cancellation
      sessionStorage.removeItem("stripe-checkout-credits");

      // Remove the query param
      setSearchParams(
        (params) => {
          const newParams = new URLSearchParams(params);
          newParams.delete("checkout");
          return newParams;
        },
        { replace: true }
      );
    }
  }, [
    checkoutQueryValue,
    setSearchParams,
    toast,
    t,
    refreshCredits,
    fetchStacks,
  ]);

  // Display pending toast when set
  useEffect(() => {
    if (pendingToast) {
      toast({
        title: t("team.credits.purchase.success.title"),
        description: t("team.credits.purchase.success.description"),
      });
      setPendingToast(null);
    }
  }, [pendingToast, toast, t]);

  // Listen for success message from checkout window (when opened via window.open)
  useEffect(() => {
    const handleCheckoutSuccess = () => {
      toast({
        title: t("team.credits.purchase.success.title"),
        description: t("team.credits.purchase.success.description"),
      });
      // Refresh credits and stacks after successful payment
      // Add a small delay to allow webhook to process
      setTimeout(() => {
        void refreshCredits();
        void fetchStacks();
      }, 2000);
    };

    window.addEventListener("stripe-checkout-success", handleCheckoutSuccess);

    return () => {
      window.removeEventListener(
        "stripe-checkout-success",
        handleCheckoutSuccess
      );
    };
  }, [toast, t, refreshCredits, fetchStacks]);

  return (
    <>
      <div className="flex mb-2">
        <Card>
          <CardContent className="flex items-center justify-between gap-8 p-6">
            <div className="flex items-center space-x-8">
              <div className="flex items-center justify-center w-12 h-12 rounded-full shadow-md bg-secondary">
                <BadgeCentIcon className="w-6 h-6 text-muted-foreground" />
              </div>

              <div className="flex flex-col">
                <span className="text-sm font-medium text-muted-foreground">
                  {t("team.credits.total")}
                </span>
                <span className="text-3xl font-bold text-foreground">
                  {isLoadingCredits ? (
                    <Loader2Icon className="w-9 h-9 animate-spin" />
                  ) : (
                    creditsBalance
                  )}
                </span>
              </div>
            </div>

            <Button
              className="px-6 py-3 rounded-full"
              onClick={() => setIsPurchaseDialogOpen(true)}
            >
              <ArrowUpIcon className="w-4 h-4 mr-2" />
              {t("team.credits.getMore")}
            </Button>
          </CardContent>
        </Card>
      </div>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between pb-8 space-y-0">
          <div>
            <CardTitle>{t("team.credits.title")}</CardTitle>
            <CardDescription>{t("team.credits.description")}</CardDescription>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <PageSkeleton />
          ) : (
            <>
              <TeamCreditsProgress
                stack={giftedStack}
                source={TeamCreditsStackSourceEnum.GIFT}
              />
              <TeamCreditsProgress
                stack={purchaseStack}
                source={TeamCreditsStackSourceEnum.PURCHASE}
              />
            </>
          )}
        </CardContent>
      </Card>

      <DialogPurchaseCredits
        open={isPurchaseDialogOpen}
        onOpenChange={setIsPurchaseDialogOpen}
        currentCredits={creditsBalance}
        onCreditsPurchased={fetchStacks}
      />
    </>
  );
}
